/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import './InputV1.scss';
import * as ReactWindow from 'react-window';
import { ChevronDown } from 'lucide-react';
import { ChevronUp } from 'lucide-react';

// Helpers to compare selected entries which might be primitives or objects
const toPrimitiveValue = (item) =>
  item && typeof item === 'object' ? item.value : item;

const DEFAULT_EMPTY_MESSAGE = 'No results';

const KEY = {
  ENTER: 'Enter',
  ESC: 'Escape',
  TAB: 'Tab',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  BACKSPACE: 'Backspace',
};

const InputV1 = ({
  // Behavior
  options = [],
  multiple = false,
  isSearchable = false,
  closeOnSelect = true,
  disabled = false,
  name,
  optionHeight = 36,
  maxVisible = 8,
  showArrows = false,

  // Values (controlled or uncontrolled)
  value,
  defaultValue,
  selectedValues, // alias for multiple
  defaultSelectedValues,

  // Presentation
  placeholder = 'Search or type…',
  className,
  style,
  searchIcon, // ReactNode or string (text/icon)
  loading = false,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  enableKeyPress = true,

  // Callbacks
  onChange,
  onInputChange,
  onSelect,
  onDeselect,
  onKeyDown,
  onFocus,
  onBlur,

  // Custom rendering
  renderOption,
  renderChip,

  // Pass-through
  inputClassName = '',
  inputProps = {},
  dropdownProps = {},
}) => {
  // Internal state
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);

  // Selected values state (supports both modes)
  const isControlledSingle = !multiple && value !== undefined;
  const isControlledMulti = multiple && Array.isArray(selectedValues);

  const [internalSingle, setInternalSingle] = useState(
    !multiple ? value ?? defaultValue ?? '' : '',
  );

  const [internalMulti, setInternalMulti] = useState(
    multiple ? selectedValues ?? defaultSelectedValues ?? [] : [],
  );

  // Keep internal in sync with controlled props
  useEffect(() => {
    if (isControlledSingle) setInternalSingle(value ?? '');
  }, [isControlledSingle, value]);

  useEffect(() => {
    if (isControlledMulti) setInternalMulti(selectedValues || []);
  }, [isControlledMulti, selectedValues]);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const getSingleValue = () =>
    isControlledSingle ? value ?? '' : internalSingle;
  const getSelectedValues = () =>
    isControlledMulti ? selectedValues || [] : internalMulti;

  const setSingleValue = (nextVal) => {
    if (disabled) return;
    if (isControlledSingle) {
      onChange && onChange(nextVal);
    } else {
      setInternalSingle(nextVal);
      onChange && onChange(nextVal);
    }
  };

  const setMultiValues = (nextArr) => {
    if (disabled) return;
    if (isControlledMulti) {
      onChange && onChange(nextArr);
    } else {
      setInternalMulti(nextArr);
      onChange && onChange(nextArr);
    }
  };

  const selectedArray = getSelectedValues();
  const singleVal = getSingleValue();

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = options;
    if (multiple && selectedArray.length > 0) {
      const selectedSet = new Set(selectedArray.map(toPrimitiveValue));
      base = base.filter((opt) => !selectedSet.has(opt.value));
    }
    if (!q) return base;
    return base.filter((opt) => String(opt.label).toLowerCase().includes(q));
  }, [options, query, multiple, selectedArray]);

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
  };

  const closeDropdown = () => setIsOpen(false);

  // Click outside to close
  useEffect(() => {
    function handleDocClick(e) {
      if (!containerRef.current) return;
      const clickedInsideContainer = containerRef.current.contains(e.target);
      const clickedInsideDropdown = dropdownRef.current
        ? dropdownRef.current.contains(e.target)
        : false;
      if (!clickedInsideContainer && !clickedInsideDropdown) {
        closeDropdown();
        setHighlightIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, []);

  // Keyboard interactions
  const handleKeyDown = (e) => {
    if (!enableKeyPress) return;
    onKeyDown && onKeyDown(e);
    if (
      !isOpen &&
      isSearchable &&
      (e.key === KEY.ARROW_DOWN || e.key === KEY.ARROW_UP)
    ) {
      openDropdown();
      setHighlightIndex(0);
      return;
    }
    switch (e.key) {
      case KEY.ARROW_DOWN: {
        if (!isSearchable || !showDropdown) return;
        e.preventDefault();
        setHighlightIndex((idx) => {
          const next = Math.min(
            (idx < 0 ? -1 : idx) + 1,
            filteredOptions.length - 1,
          );
          return next;
        });
        break;
      }
      case KEY.ARROW_UP: {
        if (!isSearchable || !showDropdown) return;
        e.preventDefault();
        setHighlightIndex((idx) => Math.max(idx - 1, 0));
        break;
      }
      case KEY.ENTER: {
        if (isSearchable) {
          if (!isOpen) return;
          e.preventDefault();
          const opt = filteredOptions[highlightIndex];
          if (opt) selectOption(opt);
        } else {
          onSelect?.(singleVal);
        }
        break;
      }
      case KEY.ESC: {
        closeDropdown();
        break;
      }
      case KEY.BACKSPACE: {
        if (multiple && query.length === 0 && selectedArray.length > 0) {
          // Remove last chip
          const last = selectedArray[selectedArray.length - 1];
          handleDeselect(last);
        }
        break;
      }
      default:
        break;
    }
  };

  const handleFocus = (e) => {
    onFocus?.(e);
    if (isSearchable) openDropdown();
  };

  const handleBlur = (e) => {
    onBlur && onBlur(e);
  };

  const selectOption = (opt) => {
    if (disabled) return;
    if (multiple) {
      const next = [...selectedArray, opt];
      setMultiValues(next);
      onSelect && onSelect(opt, next);

      setQuery('');
      setHighlightIndex(-1);
      if (inputRef.current) inputRef.current.focus();
    } else {
      setSingleValue(opt.value);
      onSelect && onSelect(opt, opt.value);
      // Clear search text so the input shows the selected label when closed
      setQuery('');
      setHighlightIndex(-1);
      if (closeOnSelect) closeDropdown();
    }
  };

  const handleDeselect = (item) => {
    if (disabled) return;
    if (!multiple) {
      setSingleValue('');
      onDeselect && onDeselect(item, '');
      return;
    }
    const next = selectedArray.filter(
      (s) => toPrimitiveValue(s) !== toPrimitiveValue(item),
    );
    setMultiValues(next);
    onDeselect && onDeselect(item, next);
    if (inputRef.current) inputRef.current.focus();
  };

  const displayedValue = useMemo(() => {
    if (multiple) return '';
    const found = options.find((o) => String(o.value) === String(singleVal));
    return found ? String(found.label) : String(singleVal ?? '');
  }, [multiple, options, singleVal]);

  const showDropdown = isSearchable && isOpen && !disabled;

  // Portal support so dropdown can escape overflow/stacking contexts
  const {
    usePortal = true,
    portalContainer = typeof document !== 'undefined' ? document.body : null,
  } = dropdownProps || {};

  // Normalize possibly null pass-through props (spreading null throws)
  const safeInputProps = inputProps || {};
  const safeDropdownProps = dropdownProps || {};

  const [portalStyle, setPortalStyle] = useState({ top: 0, left: 0, width: 0 });
  const listRef = useRef(null);
  const [measuredHeights, setMeasuredHeights] = useState([]);

  // Measure option heights dynamically (when not using custom renderer)
  useEffect(() => {
    if (!showDropdown) return;
    // If custom renderOption is provided, respect fixed optionHeight for all
    if (renderOption) {
      const count = filteredOptions.length;
      const heights = Array(count).fill(optionHeight || 36);
      setMeasuredHeights(heights);
      // Reset sizes in list so it recalculates
      if (listRef.current) listRef.current.resetAfterIndex(0, true);
      return;
    }

    // For plain text options, measure actual height based on content/width
    const containerWidth =
      portalStyle.width || (containerRef.current?.offsetWidth ?? 0);
    const temp = document.createElement('div');
    temp.style.position = 'fixed';
    temp.style.visibility = 'hidden';
    temp.style.pointerEvents = 'none';
    temp.style.zIndex = '-1';
    temp.style.left = '-10000px';
    temp.style.top = '-10000px';
    temp.style.width = `${containerWidth}px`;
    // Mirror option styles likely affecting height
    temp.className = 'inputv1__option';
    document.body.appendChild(temp);

    const heights = filteredOptions.map((opt) => {
      temp.textContent = String(opt.label ?? '');
      const h = temp.offsetHeight || optionHeight || 36;
      // Ensure a sensible minimum height
      return Math.max(28, h);
    });

    document.body.removeChild(temp);
    setMeasuredHeights(heights);
    if (listRef.current) listRef.current.resetAfterIndex(0, true);
  }, [
    showDropdown,
    filteredOptions,
    renderOption,
    optionHeight,
    portalStyle.width,
  ]);

  const updatePortalPosition = useCallback(() => {
    if (!usePortal || !showDropdown || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const top = rect.bottom + (window.scrollY || window.pageYOffset);
    const left = rect.left + (window.scrollX || window.pageXOffset);
    const width = rect.width;
    setPortalStyle({ top, left, width });
  }, [usePortal, showDropdown]);

  useEffect(() => {
    updatePortalPosition();
  }, [showDropdown, updatePortalPosition]);

  useEffect(() => {
    if (!usePortal) return;
    const handler = () => updatePortalPosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [usePortal, updatePortalPosition]);

  const mergedInputProps = {
    name,
    disabled,
    placeholder: multiple
      ? selectedArray.length
        ? ''
        : placeholder
      : placeholder,
    // When searchable: show query while dropdown is open; otherwise show selected label
    value: isSearchable
      ? showDropdown
        ? query
        : displayedValue
      : displayedValue,
    onChange: (e) => {
      const next = e.target.value;
      if (isSearchable) {
        setQuery(next);
        onInputChange && onInputChange(next);
        if (!isOpen) openDropdown();
      } else {
        setSingleValue(next);
      }
    },
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    autoComplete: 'off',
    ...safeInputProps,
  };

  return (
    <div
      ref={containerRef}
      className={classNames('inputv1', className, {
        'inputv1--disabled': disabled,
        'inputv1--open': showDropdown,
        'inputv1--multiple': multiple,
      })}
      style={style}
      onClick={() => {
        if (disabled) return;
        if (inputRef.current) inputRef.current.focus();
      }}
    >
      {/* Input field with Icon */}
      <div
        className={`inputv1__control ${inputClassName || ''}`}
        onClick={() => {
          if (isSearchable) openDropdown();
        }}
      >
        {searchIcon ? (
          <div className='inputv1__icon' aria-hidden>
            {typeof searchIcon === 'string' ? (
              <span>{searchIcon}</span>
            ) : (
              searchIcon
            )}
          </div>
        ) : null}

        {multiple && selectedArray.length > 0 && (
          <div className='inputv1__chips'>
            {selectedArray?.map((item) => {
              const label = typeof item === 'object' ? item.label : item;
              const key = typeof item === 'object' ? item.value : item;
              return (
                <div className='inputv1__chip' key={String(key)}>
                  {renderChip ? (
                    renderChip(item, () => handleDeselect(item))
                  ) : (
                    <>
                      <span className='inputv1__chip-label'>
                        {String(label)}
                      </span>
                      <button
                        type='button'
                        className='inputv1__chip-remove'
                        aria-label={`Remove ${String(label)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeselect(item);
                        }}
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <input
          data-dd-privacy='mask'
          ref={inputRef}
          className='inputv1__input'
          {...mergedInputProps}
        />
        {showArrows ? (
          !showDropdown ? (
            <ChevronDown
              size={20}
              className='tw-text-grey-800 tw-cursor-auto'
            />
          ) : (
            <ChevronUp size={20} className=' tw-text-grey-800 tw-cursor-auto' />
          )
        ) : null}
      </div>

      {/* Dropdown */}
      {showDropdown && usePortal && portalContainer
        ? ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              className={classNames(
                'inputv1__dropdown',
                'inputv1__dropdown--portal',
                safeDropdownProps.className,
              )}
              role='listbox'
              style={{
                position: 'fixed',
                top: portalStyle.top,
                left: portalStyle.left,
                width: portalStyle.width,
              }}
              {...safeDropdownProps}
            >
              {loading ? (
                <div className='inputv1__option inputv1__option--disabled'>
                  Loading…
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className='inputv1__option inputv1__option--disabled'>
                  {emptyMessage}
                </div>
              ) : (
                (() => {
                  const defaultItemSize = optionHeight;
                  const visibleCount = Math.min(
                    filteredOptions.length,
                    maxVisible,
                  );
                  const height =
                    measuredHeights.length === filteredOptions.length
                      ? measuredHeights
                          .slice(0, visibleCount)
                          .reduce((a, b) => a + b, 0)
                      : visibleCount * defaultItemSize;

                  const Row = ({ index, style }) => {
                    const opt = filteredOptions?.[index];
                    const active = index === highlightIndex;
                    return (
                      <div
                        key={String(opt?.value)}
                        style={style}
                        aria-selected={active}
                        className={classNames('inputv1__option', {
                          'inputv1__option--active': active,
                        })}
                        onMouseEnter={() => setHighlightIndex(index)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectOption(opt);
                        }}
                      >
                        {renderOption ? renderOption(opt, active) : opt?.label}
                      </div>
                    );
                  };

                  const ModernList = ReactWindow.List;
                  if (ModernList) {
                    return (
                      <ModernList
                        ref={listRef}
                        rowCount={filteredOptions?.length}
                        rowHeight={(index) =>
                          measuredHeights[index] ?? defaultItemSize
                        }
                        rowProps={{}}
                        rowComponent={Row}
                        style={{
                          height,
                          width:
                            portalStyle.width ||
                            (containerRef.current?.offsetWidth ?? 0),
                        }}
                      />
                    );
                  }
                  const LegacyList = ReactWindow.VariableSizeList
                    ? ReactWindow.VariableSizeList
                    : ReactWindow.FixedSizeList;
                  return (
                    <LegacyList
                      ref={listRef}
                      height={height}
                      itemCount={filteredOptions?.length}
                      itemSize={(index) =>
                        measuredHeights[index] ?? defaultItemSize
                      }
                      width={
                        portalStyle.width ||
                        (containerRef.current?.offsetWidth ?? 0)
                      }
                    >
                      {Row}
                    </LegacyList>
                  );
                })()
              )}
            </div>,
            portalContainer,
          )
        : null}
    </div>
  );
};

export default InputV1;
